import { useCallback, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Loader2, Waypoints } from 'lucide-react';
import { adminReceiptPaidTraceAPI } from '../../lib/adminReceiptPaidTraceApi';
import { getErrorMessage } from '../../lib/errors';
import { useJobPolling, useJobResume, JobProgressLine, JobErrorBlock } from './JobPollingPanel';

const STAGE_LABELS = {
  invoice_signal: 'Invoice has search signal',
  already_reconciled_externally: 'Already reconciled externally',
  already_reconciled_pool_miss: 'Reconciled-pool pre-check (no hit)',
  accounts_searched: 'Accounts searched',
  transactions_in_window: 'Transactions in date window',
  direction_sign: 'Direction / sign',
  amount_fx_tolerance: 'Amount + FX tolerance',
  vendor_match: 'Vendor match',
  ambiguity_detail: 'Ambiguity detail (deterministic tiering)',
};

const TIER_LABELS = {
  tier1_own_entity: 'Tier 1 (own entity)',
  tier2_all_entities: 'Tier 2 (all entities)',
};

// Prompt AB item 3: which anchor decided a STAGE_TRANSACTIONS_IN_WINDOW
// kill's search window -- see services/receipt_paid_reconciliation.py's
// window_kill_by_anchor. Keys mirror ANCHOR_EMAIL_DATE / ANCHOR_DUE_DATE /
// ANCHOR_EMAIL_DATE_UNPAID_UNDATED exactly.
const ANCHOR_LABELS = {
  email_date: 'Email-date window (paid-pending)',
  due_date: 'Due-date window (unpaid-overdue)',
  email_date_unpaid_undated: 'Email-date window (unpaid, undated)',
};

function StageRow({ stage }) {
  const label = STAGE_LABELS[stage.stage] || stage.stage;
  const isAlreadyReconciled = stage.stage === 'already_reconciled_externally';
  const zeroed = !stage.informational && stage.stage !== 'ambiguity_detail' && !isAlreadyReconciled && stage.count_survived === 0;
  return (
    <div
      className={`p-2 rounded border text-xs space-y-1 ${zeroed ? 'bg-red-50 border-red-200 dark:bg-red-950/30 dark:border-red-900' : 'border-border'} ${stage.stage === 'ambiguity_detail' ? 'bg-amber-50 border-amber-200 dark:bg-amber-950/30 dark:border-amber-900' : ''} ${isAlreadyReconciled ? 'bg-blue-50 border-blue-200 dark:bg-blue-950/30 dark:border-blue-900' : ''}`}
      data-testid={`trace-stage-${stage.stage}`}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="font-medium">{label}</span>
        {stage.informational ? (
          <Badge variant="outline" className="text-[10px]">informational</Badge>
        ) : stage.stage === 'ambiguity_detail' ? (
          <Badge variant="outline" className="text-[10px]" data-testid="trace-ambiguity-tier-badge">
            {TIER_LABELS[stage.tier] || stage.tier} &middot; {stage.candidate_count} candidates
          </Badge>
        ) : isAlreadyReconciled ? (
          <Badge variant="outline" className="text-[10px]" data-testid="trace-already-reconciled-badge">
            {TIER_LABELS[stage.tier] || stage.tier}
          </Badge>
        ) : (
          <Badge variant={zeroed ? 'destructive' : 'outline'} className="text-[10px]">
            {stage.count_survived}{stage.count_in != null ? ` / ${stage.count_in}` : ''} survived
          </Badge>
        )}
      </div>

      {stage.stage === 'invoice_signal' && stage.missing_fields?.length > 0 && (
        <p className="text-muted-foreground">missing: {stage.missing_fields.join(', ')}</p>
      )}

      {isAlreadyReconciled && (
        <div className="text-muted-foreground space-y-1" data-testid="trace-already-reconciled-detail">
          <p>
            tx {stage.transaction_id} &middot; {stage.date} &middot; {stage.amount} {stage.currency} &middot;{' '}
            {stage.counterparty}
          </p>
          <p>
            The real settling transaction is already reconciled, just not against this receipt --
            every stage below was skipped, it would only ever have traced unrelated noise.
          </p>
          {stage.reconciled_by?.sibling_receipt_id ? (
            <p>reconciled against sibling receipt {stage.reconciled_by.sibling_receipt_id}</p>
          ) : stage.reconciled_by?.pointer_value ? (
            <p>
              reconciled_by {stage.reconciled_by.kind || stage.reconciled_by.pointer_field}:{' '}
              {stage.reconciled_by.pointer_value}
            </p>
          ) : null}
        </div>
      )}

      {stage.stage === 'already_reconciled_pool_miss' && (
        <div className="text-muted-foreground space-y-1" data-testid="trace-reconciled-pool-miss-detail">
          <p>
            {stage.in_scope_count} reconciled transaction(s) in the same date-window scope
            {stage.scan_truncated && (
              <Badge variant="destructive" className="text-[10px] ml-1">scan truncated</Badge>
            )}
          </p>
          {stage.passed_all_gates_count > 0 ? (
            <p>
              {stage.passed_all_gates_count} candidate(s) passed every gate but couldn't be uniquely
              resolved (ambiguous) -- see the funnel's cohort/ambiguity handling below.
            </p>
          ) : stage.nearest_miss ? (
            <p data-testid="trace-reconciled-pool-nearest-miss">
              nearest reconciled miss: tx {stage.nearest_miss.transaction_id} ({stage.nearest_miss.counterparty})
              &mdash; killed by <span className="font-mono">{stage.nearest_miss.killed_by}</span>
              {stage.nearest_miss.killed_by === 'window' && stage.nearest_miss.hours_outside_window != null && (
                <> ({stage.nearest_miss.hours_outside_window}h outside window)</>
              )}
              {stage.nearest_miss.killed_by === 'amount_fx' && stage.nearest_miss.amount_delta != null && (
                <> (delta {stage.nearest_miss.amount_delta} vs target {stage.nearest_miss.target_amount})</>
              )}
              {stage.nearest_miss.killed_by === 'vendor' && stage.nearest_miss.vendor_similarity != null && (
                <> ("{stage.nearest_miss.counterparty_normalized}", {(stage.nearest_miss.vendor_similarity * 100).toFixed(0)}% similar)</>
              )}
            </p>
          ) : (
            <p>no reconciled transaction in scope at all -- widening the window would not help here.</p>
          )}
        </div>
      )}

      {stage.stage === 'accounts_searched' && (
        <p className="text-muted-foreground">
          scope: {stage.entity_scope} &middot; {stage.count} account(s)
          {stage.accounts?.length > 0 && (
            <> &mdash; {stage.accounts.map((a) => `${a.bank_name}/${a.account_name} (${a.entity_id})`).join(', ')}</>
          )}
        </p>
      )}

      {stage.stage === 'transactions_in_window' && stage.nearest_miss && (
        <p className="text-muted-foreground" data-testid="trace-window-nearest-miss">
          nearest miss: tx {stage.nearest_miss.transaction_id} ({stage.nearest_miss.counterparty}) &mdash;{' '}
          {stage.nearest_miss.hours_outside_window}h ({stage.nearest_miss.days_outside_window}d) {stage.nearest_miss.direction === 'before_window' ? 'before' : 'after'} the window
        </p>
      )}

      {stage.stage === 'direction_sign' && (
        <p className="text-muted-foreground">
          {stage.outbound_negative} outbound (negative), {stage.inbound_positive} inbound (positive) &mdash; {stage.note}
        </p>
      )}

      {stage.stage === 'amount_fx_tolerance' && (
        <div className="text-muted-foreground space-y-1">
          <p>target: {stage.target_amount} {stage.target_currency}
            {stage.fx_unavailable_count > 0 && <> &middot; {stage.fx_unavailable_count} skipped (no FX rate)</>}
            {stage.direction_killed_count > 0 && <> &middot; {stage.direction_killed_count} skipped (inbound, not an outflow)</>}
          </p>
          {stage.nearest_miss && (
            <p>
              nearest miss: tx {stage.nearest_miss.transaction_id} ({stage.nearest_miss.counterparty}) &mdash;{' '}
              {stage.nearest_miss.amount} {stage.nearest_miss.currency} vs target {stage.nearest_miss.target_amount}, delta {stage.nearest_miss.delta}
              {stage.nearest_miss.cross_currency ? ' (cross-currency)' : ''}
            </p>
          )}
        </div>
      )}

      {stage.stage === 'vendor_match' && (
        <div className="text-muted-foreground space-y-1">
          <p>invoice vendor (normalized): <span className="font-mono">{stage.invoice_vendor_normalized}</span></p>
          {stage.nearest_counterparties?.length > 0 && (
            <ul className="space-y-0.5">
              {stage.nearest_counterparties.map((c) => (
                <li key={c.transaction_id} className="font-mono">
                  {(c.similarity * 100).toFixed(0)}% &middot; "{c.counterparty_raw}" &rarr; "{c.counterparty_normalized}" (tx {c.transaction_id})
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {stage.stage === 'ambiguity_detail' && (
        <div className="text-muted-foreground space-y-1">
          <p>
            More than one candidate in the deciding tier, and the gap to the next-closest date
            didn't exceed 24h -- refuses rather than guessing. Tied candidates:
          </p>
          {stage.tied_candidates?.length > 0 && (
            <ul className="space-y-0.5" data-testid="trace-ambiguity-tied-candidates">
              {stage.tied_candidates.map((c) => (
                <li key={c.transaction_id} className="font-mono">
                  tx {c.transaction_id} &middot; entity {c.entity_id} &middot;{' '}
                  {c.date_delta_hours != null ? `${c.date_delta_hours}h from email date` : 'date unparseable'}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

const FUNNEL_STAGE_ORDER = ['invoice_signal', 'transactions_in_window', 'amount_fx_tolerance', 'vendor_match'];

export function ReceiptPaidTracePanel() {
  const [invoiceId, setInvoiceId] = useState('');
  const [traceResult, setTraceResult] = useState(null);
  const [traceStarting, setTraceStarting] = useState(false);
  const [traceFailed, setTraceFailed] = useState(null);

  const [funnelResult, setFunnelResult] = useState(null);
  const [funnelStarting, setFunnelStarting] = useState(false);
  const [funnelFailed, setFunnelFailed] = useState(null);
  const [funnelProgress, setFunnelProgress] = useState(null);

  const tracePoll = useJobPolling({
    fetchStatus: adminReceiptPaidTraceAPI.traceJobStatus,
    onCompleted: useCallback((result) => {
      setTraceResult(result);
      setTraceFailed(null);
    }, []),
    onFailed: useCallback((error) => {
      setTraceFailed(error);
    }, []),
  });

  const funnelPoll = useJobPolling({
    fetchStatus: adminReceiptPaidTraceAPI.funnelSummaryJobStatus,
    onCompleted: useCallback((result) => {
      setFunnelResult(result);
      setFunnelFailed(null);
      setFunnelProgress(null);
    }, []),
    onFailed: useCallback((error) => {
      setFunnelFailed(error);
      setFunnelProgress(null);
    }, []),
    onProgress: useCallback((progress) => {
      setFunnelProgress(progress);
    }, []),
  });

  useJobResume({ fetchLatest: adminReceiptPaidTraceAPI.funnelSummaryJobLatest, poll: funnelPoll });

  const runTrace = async () => {
    if (!invoiceId.trim()) return;
    setTraceStarting(true);
    setTraceFailed(null);
    setTraceResult(null);
    try {
      const { data } = await adminReceiptPaidTraceAPI.traceStart(invoiceId.trim());
      if (data.job_id) {
        tracePoll.start(data.job_id);
      }
    } catch (err) {
      setTraceFailed(getErrorMessage(err) || 'Failed to start trace.');
    } finally {
      setTraceStarting(false);
    }
  };

  const traceBusy = traceStarting || tracePoll.polling;

  const runFunnelSummary = async () => {
    setFunnelStarting(true);
    setFunnelFailed(null);
    setFunnelResult(null);
    setFunnelProgress(null);
    try {
      const { data } = await adminReceiptPaidTraceAPI.funnelSummaryStart();
      if (data.job_id) {
        funnelPoll.start(data.job_id);
      }
    } catch (err) {
      setFunnelFailed(getErrorMessage(err) || 'Failed to start funnel summary.');
    } finally {
      setFunnelStarting(false);
    }
  };

  const funnelBusy = funnelStarting || funnelPoll.polling;

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2">
          <Waypoints className="h-4 w-4 text-muted-foreground" />
          <CardTitle className="text-base">Receipt-Paid Match Funnel Trace</CardTitle>
        </div>
        <p className="text-xs text-muted-foreground">
          Report-only instrumentation of the Receipt-Paid matcher (services/receipt_paid_reconciliation.py).
          Never changes matching logic or writes anything. Trace one gmail receipt id to see which stage
          (date window, amount/FX tolerance, vendor match) kills its candidate search -- or that its true
          settling transaction is already reconciled elsewhere, which excludes it from the pool before any
          stage runs -- or run the Funnel Summary to bucket the whole Seen population by the stage that
          zeroed it.
        </p>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="space-y-2">
          <div className="flex gap-2">
            <Input
              value={invoiceId}
              onChange={(e) => setInvoiceId(e.target.value)}
              placeholder="gmail receipt / invoice id"
              className="h-8 text-xs"
              data-testid="receipt-paid-trace-invoice-id-input"
            />
            <Button
              variant="outline" size="sm" onClick={runTrace} disabled={traceBusy || !invoiceId.trim()}
              data-testid="receipt-paid-trace-run"
            >
              {traceBusy ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
              Trace
            </Button>
          </div>

          <JobErrorBlock error={traceFailed} testId="receipt-paid-trace-error" />

          {traceResult && (
            traceResult.error ? (
              <p className="text-xs text-red-600" data-testid="receipt-paid-trace-not-found">
                {traceResult.error === 'invoice_not_found' ? `Invoice ${traceResult.invoice_id} not found.` : traceResult.error}
              </p>
            ) : (
              <div className="space-y-2" data-testid="receipt-paid-trace-result">
                <div className="flex flex-wrap gap-2 text-xs">
                  <span>Supplier: <strong>{traceResult.invoice?.supplier_raw}</strong></span>
                  <span>Amount: <strong>{traceResult.invoice?.amount} {traceResult.invoice?.currency}</strong></span>
                  <span>Entity: <strong>{traceResult.invoice?.entity_id}</strong></span>
                  {traceResult.zero_stage ? (
                    <Badge variant="destructive" className="text-[10px]" data-testid="receipt-paid-trace-zero-stage">
                      Dies at: {STAGE_LABELS[traceResult.zero_stage] || traceResult.zero_stage}
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="text-[10px]">
                      {traceResult.final_candidate_count} candidate(s) survive
                    </Badge>
                  )}
                </div>
                <div className="space-y-1.5">
                  {traceResult.stages?.map((stage) => (
                    <StageRow key={stage.stage} stage={stage} />
                  ))}
                </div>
              </div>
            )
          )}
        </div>

        <div className="space-y-2 border-t pt-3">
          <div className="flex items-center justify-between">
            <p className="text-xs font-medium">Funnel summary (whole Seen population)</p>
            <Button
              variant="outline" size="sm" onClick={runFunnelSummary} disabled={funnelBusy}
              data-testid="receipt-paid-trace-funnel-summary-run"
            >
              {funnelBusy ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
              Run Funnel Summary
            </Button>
          </div>

          {funnelPoll.polling && (
            <JobProgressLine progress={funnelProgress} testId="receipt-paid-trace-funnel-summary-progress">
              {(p) => <>Scanned {p.scanned ?? 0} of {p.total ?? '?'} ({p.seen ?? 0} in population)</>}
            </JobProgressLine>
          )}

          <JobErrorBlock error={funnelFailed} testId="receipt-paid-trace-funnel-summary-error" />

          {funnelResult && (
            <div className="space-y-2" data-testid="receipt-paid-trace-funnel-summary-result">
              <div className="flex flex-wrap gap-2 text-xs">
                <Badge variant="outline" className="text-[10px]">Seen: {funnelResult.seen}</Badge>
                {funnelResult.population_breakdown && (
                  <Badge variant="outline" className="text-[10px]" data-testid="receipt-paid-trace-funnel-population-breakdown">
                    Paid-pending: {funnelResult.population_breakdown.paid_pending_verification ?? 0}
                    {' '}&middot; Unpaid-overdue: {funnelResult.population_breakdown.unpaid_overdue ?? 0}
                  </Badge>
                )}
                {funnelResult.already_reconciled_externally?.count > 0 && (
                  <Badge
                    variant="outline" className="text-[10px] bg-blue-50 dark:bg-blue-950/30"
                    data-testid="receipt-paid-trace-funnel-already-reconciled"
                  >
                    Already reconciled (excluded from pool): {funnelResult.already_reconciled_externally.count}
                  </Badge>
                )}
                <Badge variant="outline" className="text-[10px]">
                  Reconciled: {funnelResult.outcome_counts?.reconciled_single_candidate ?? 0}
                </Badge>
                <Badge variant="outline" className="text-[10px]">
                  Ambiguous: {funnelResult.outcome_counts?.ambiguous_multi_candidate ?? 0}
                </Badge>
                {funnelResult.outcome_counts?.reconciled_tie_broken > 0 && (
                  <Badge variant="outline" className="text-[10px]" data-testid="receipt-paid-trace-funnel-tie-broken">
                    Tie-broken: {funnelResult.outcome_counts.reconciled_tie_broken}
                  </Badge>
                )}
                {funnelResult.outcome_counts?.reconciled_cohort_assignment > 0 && (
                  <Badge variant="outline" className="text-[10px]" data-testid="receipt-paid-trace-funnel-cohort-assignment">
                    Cohort-assigned: {funnelResult.outcome_counts.reconciled_cohort_assignment}
                  </Badge>
                )}
                {funnelResult.outcome_counts?.cohort_unbalanced > 0 && (
                  <Badge variant="outline" className="text-[10px]" data-testid="receipt-paid-trace-funnel-cohort-unbalanced">
                    Cohort unbalanced: {funnelResult.outcome_counts.cohort_unbalanced}
                  </Badge>
                )}
                {funnelResult.skipped_invoices?.count > 0 && (
                  <Badge
                    variant="destructive" className="text-[10px]"
                    data-testid="receipt-paid-trace-funnel-skipped-invoices"
                  >
                    Skipped (malformed): {funnelResult.skipped_invoices.count}
                  </Badge>
                )}
              </div>
              {funnelResult.skipped_invoices?.examples?.length > 0 && (
                <ul className="text-xs space-y-0.5" data-testid="receipt-paid-trace-funnel-skipped-examples">
                  {funnelResult.skipped_invoices.examples.map((e) => (
                    <li key={e.invoice_id} className="font-mono text-red-600 dark:text-red-400">
                      {e.invoice_id || '(no id)'} &middot; {e.supplier} &middot; {e.error}
                    </li>
                  ))}
                </ul>
              )}
              {funnelResult.already_reconciled_externally?.examples?.length > 0 && (
                <ul className="text-xs space-y-0.5" data-testid="receipt-paid-trace-funnel-already-reconciled-examples">
                  {funnelResult.already_reconciled_externally.examples.map((e) => (
                    <li key={e.invoice_id} className="font-mono text-blue-700 dark:text-blue-400">
                      {e.invoice_id} &middot; {e.supplier} &middot; already reconciled via tx {e.transaction_id}
                      {e.reconciled_by?.sibling_receipt_id && (
                        <> &middot; sibling receipt {e.reconciled_by.sibling_receipt_id}</>
                      )}
                    </li>
                  ))}
                </ul>
              )}
              {funnelResult.ambiguous_examples?.length > 0 && (
                <ul className="text-xs space-y-0.5" data-testid="receipt-paid-trace-funnel-ambiguous-examples">
                  {funnelResult.ambiguous_examples.map((e) => (
                    <li key={e.invoice_id} className="font-mono text-amber-700 dark:text-amber-400">
                      {e.invoice_id} &middot; {e.supplier} &middot; {e.candidate_count} candidates ({TIER_LABELS[e.tier] || e.tier})
                    </li>
                  ))}
                </ul>
              )}
              <div className="space-y-1">
                {FUNNEL_STAGE_ORDER.map((stage) => {
                  const count = funnelResult.stage_zero_counts?.[stage] ?? 0;
                  const pct = funnelResult.seen ? Math.round((count / funnelResult.seen) * 100) : 0;
                  const nm = funnelResult.nearest_miss_summary?.[stage];
                  return (
                    <div
                      key={stage}
                      className="p-1.5 rounded border text-xs space-y-0.5"
                      data-testid={`receipt-paid-trace-funnel-summary-stage-${stage}`}
                    >
                      <div className="flex items-center justify-between">
                        <span>{STAGE_LABELS[stage] || stage}</span>
                        <span className={count > 0 ? 'font-medium text-red-600 dark:text-red-400' : 'text-muted-foreground'}>
                          {count} ({pct}%)
                        </span>
                      </div>
                      {/* Nearest-miss distance -- so this run says whether the -72h/+48h
                          window or the amount/FX band needs moving, and by how much. */}
                      {stage === 'transactions_in_window' && nm?.hours_outside_window && (
                        <p className="text-muted-foreground" data-testid="receipt-paid-trace-funnel-window-nearest-miss">
                          nearest miss: {nm.hours_outside_window.min}h&ndash;{nm.hours_outside_window.max}h outside window
                          (median {nm.hours_outside_window.median}h, n={nm.hours_outside_window.count})
                          {nm.no_vendor_matching_transaction_in_scan_range > 0 && (
                            <> &middot; {nm.no_vendor_matching_transaction_in_scan_range} with no vendor match in scan range</>
                          )}
                        </p>
                      )}
                      {stage === 'transactions_in_window' && funnelResult.window_kill_by_anchor && (
                        <ul className="text-muted-foreground space-y-0.5" data-testid="receipt-paid-trace-funnel-window-kill-by-anchor">
                          {Object.entries(funnelResult.window_kill_by_anchor)
                            .filter(([, killCount]) => killCount > 0)
                            .map(([anchor, killCount]) => (
                              <li key={anchor}>{ANCHOR_LABELS[anchor] || anchor}: {killCount}</li>
                            ))}
                        </ul>
                      )}
                      {stage === 'amount_fx_tolerance' && nm?.delta && (
                        <p className="text-muted-foreground" data-testid="receipt-paid-trace-funnel-amount-nearest-miss">
                          nearest miss: {nm.delta.min}&ndash;{nm.delta.max} delta (median {nm.delta.median}, n={nm.delta.count})
                          {nm.cross_currency_count > 0 && <> &middot; {nm.cross_currency_count} cross-currency</>}
                          {nm.fx_rate_unavailable_receipts > 0 && (
                            <> &middot; {nm.fx_rate_unavailable_receipts} with no FX rate available</>
                          )}
                        </p>
                      )}
                      {stage === 'vendor_match' && funnelResult.vendor_kill_breakdown?.length > 0 && (
                        <ul className="space-y-0.5" data-testid="receipt-paid-trace-funnel-vendor-kill-breakdown">
                          {funnelResult.vendor_kill_breakdown.map((v) => (
                            <li key={v.vendor_normalized} className="font-mono text-muted-foreground">
                              {v.vendor_raw_example} &middot; {v.receipt_count} receipt(s)
                              {v.top_nearest_counterparty_raw && (
                                <> &mdash; nearest: "{v.top_nearest_counterparty_raw}" ({(v.top_similarity * 100).toFixed(0)}%)</>
                              )}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
